import * as yup from "yup";
import { CustomFieldLabelSchema } from "./customFieldLabel";
import { AI_VISIBILITY } from "../enums";
import {
  BLANK_TIPTAP_JSON,
  PlainTextFieldSchema,
  TipTapFieldSchema,
} from "./tipTapField";

export { BLANK_HTML, BLANK_TIPTAP_JSON, extractTextFromTipTap, MAX_NUM_CHARACTERS_PER_FIELD } from "./tipTapField";

export const MAX_NUM_CHARACTERS_PER_PROJECT = 2_000; // characters here means fictional entities, not text
export const MAX_NUM_CUSTOM_FIELDS = 2_000;
export { MAX_NUM_CHARACTERS_PER_FIELD_LABEL } from "./customFieldLabel";

export function generateId(length = 5) {
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;
  let result = "";
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}

export const CustomFieldSchema = yup.object().shape({
  id: yup
    .string()
    .required()
    .default(() => generateId(10)),
  label: CustomFieldLabelSchema,
  value: TipTapFieldSchema.default(BLANK_TIPTAP_JSON),
  aiVisibility: yup.string().oneOf(Object.values(AI_VISIBILITY)).required().default(AI_VISIBILITY.AUTO),
});

export const characterField = (label) => CustomFieldSchema.cast({ label });

export const DEFAULT_CHARACTER_ROLE_TEMPLATES = [
  {
    name: "Protagonist",
    optionValue: "protagonist",
    description: "The main character or leading figure in your story, often seen as the hero.",
    defaultFields: [
      characterField("Personality"),
      characterField("Motivations"),
      characterField("Internal Conflict"),
      characterField("Strengths"),
      characterField("Weaknesses"),
      characterField("Character Arc"),
      characterField("Physical Description"),
      characterField("Dialogue Style"),
    ],
  },
  {
    name: "Antagonist",
    optionValue: "antagonist",
    description: "A character or force in conflict with the protagonist, often viewed as the villain or obstacle.",
    defaultFields: [
      characterField("Motivations"),
      characterField("Methods"),
      characterField("Power & Resources"),
      characterField("Weaknesses"),
      characterField("Relationship to Protagonist"),
      characterField("Physical Description"),
      characterField("Dialogue Style"),
    ],
  },
  {
    name: "Supporting Character",
    optionValue: "supportingCharacter",
    description: "A character who is not the main focus of the story, but supports the protagonist.",
    defaultFields: [
      characterField("Personality"),
      characterField("Skills or Resources"),
      characterField("Relationship to Protagonist"),
      characterField("Role in Story"),
      characterField("Physical Description"),
      characterField("Dialogue Style"),
    ],
  },
  {
    name: "Minor Character",
    optionValue: "minorCharacter",
    description: "A character who plays a small role in the story.",
    defaultFields: [
      characterField("Description"),
      characterField("Function in Story"),
      characterField("Notable Detail"),
      characterField("Physical Description"),
      characterField("Dialogue Style"),
    ],
  },
  {
    name: "Love Interest",
    optionValue: "loveInterest",
    description: "A character who is romantically involved with the protagonist.",
    defaultFields: [
      characterField("Personality"),
      characterField("Chemistry with Protagonist"),
      characterField("Romantic Obstacles"),
      characterField("Shared History"),
      characterField("Relationship Dynamics"),
      characterField("Physical Description"),
      characterField("Dialogue Style"),
    ],
  },
];

export const DEFAULT_ROLE_OPTIONS = DEFAULT_CHARACTER_ROLE_TEMPLATES.map(({ optionValue, name, description }) => ({
  value: optionValue,
  label: name,
  description,
}));

export const DEFAULT_CHARACTER_CUSTOM_FIELDS = [
  characterField("Personality"),
  characterField("Background"),
  characterField("Physical Description"),
  characterField("Dialogue Style"),
];

// this is the frontend structure, for validating the form
// we only deal with plaintext values on the server
export const CharacterSchema = yup.object().shape({
  userId: yup.string().required(),
  projectId: yup.string().required(),
  name: TipTapFieldSchema.default(BLANK_TIPTAP_JSON),
  roles: PlainTextFieldSchema,
  pronouns: TipTapFieldSchema.default(BLANK_TIPTAP_JSON),
  groups: TipTapFieldSchema.default(BLANK_TIPTAP_JSON),
  otherNames: TipTapFieldSchema.default(BLANK_TIPTAP_JSON),
  customFields: yup.array().of(CustomFieldSchema).max(MAX_NUM_CUSTOM_FIELDS).default(DEFAULT_CHARACTER_CUSTOM_FIELDS),
  createdAt: yup
    .date()
    .required()
    .default(() => new Date()),
  updatedAt: yup.date().notRequired(),
  deletedAt: yup.date().notRequired(),
  displayOrder: yup.number().required().default(10),
  collapsed: yup.bool().required().default(false),
  aiVisibility: yup.string().oneOf(Object.values(AI_VISIBILITY)).required().default(AI_VISIBILITY.AUTO),
  seriesId: yup.string().notRequired(),
});
